import type {
    LoginRequest,
    LoginResponse,
    PlaylistItemRequest,
    SendStateRequest,
    SendTrackingRequest,
} from './index';
import type { Playlist, PlaylistItemContent } from '../playlist';

export class RemoteClient {
    login: LoginRequest;
    accessToken: string = '';
    streamingUrl: string = '';
    streamingToken: string = '';

    constructor(login: LoginRequest) {
        this.login = login;
    }

    async sleep(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    async fetch(
        url: string,
        init: RequestInit,
        retries: number = 3,
    ): Promise<any> {
        for (let retry = 0; retry < retries; retry++) {
            try {
                if (!this.accessToken) {
                    await this.fetchToken();
                }

                init.headers = {
                    ...init.headers,
                    authorization: `Bearer ${this.accessToken}`,
                };

                const response = await fetch(url, init);

                if (response.status === 401 || response.status === 422) {
                    console.warn(
                        '401 Unauthorized: Token wird aktualisiert... Warte 1s.',
                    );
                    await this.sleep(1000);
                    await this.fetchToken();
                    continue; // Erneut versuchen mit dem neuen Token
                }

                if (!response.ok) {
                    throw new Error('Fehler beim Abrufen des JWT-Tokens');
                }

                return await response.json();
            } catch (error) {
                if (retry < retries - 1) {
                    console.warn(
                        `Versuch ${
                            retry + 1
                        } fehlgeschlagen. Warte 1s. Erneuter Versuch...`,
                    );
                    await this.sleep(1000);
                } else {
                    console.error('Alle Versuche fehlgeschlagen.');
                    throw error;
                }
            }
        }
    }

    async fetchToken(): Promise<void> {
        if (
            !this.login.user ||
            !this.login.pass ||
            !this.login.cid ||
            !this.login.pid
        ) {
            throw new Error('Fehlende Logindaten');
        }

        const formData = new URLSearchParams();
        formData.append('username', this.login.user);
        formData.append('password', this.login.pass);

        const response = await fetch(`https://cm.livespotting.com/api/login`, {
            method: 'POST',
            headers: {
                accept: 'application/json',
                'content-type': 'application/x-www-form-urlencoded',
            },
            body: formData.toString(),
        });

        if (!response.ok) {
            throw new Error('Fehler beim Abrufen des JWT-Tokens');
        }

        const loginData = (await response.json()) as LoginResponse;

        if (loginData.access_token.length === 0) {
            throw new Error('Access token fehlt');
        }

        if (
            loginData.customer.streaming_url.length === 0 ||
            loginData.customer.streaming_token.length === 0
        ) {
            throw new Error('Fehlerhafte Streamingdaten: URL und Token fehlen');
        }

        this.accessToken = loginData.access_token;
        this.streamingUrl = loginData.customer.streaming_url;
        this.streamingToken = loginData.customer.streaming_token;
    }

    async fetchPlaylist(): Promise<Playlist> {
        let playlist = await this.fetch(
            `https://cm.livespotting.com/api/v1/customer/${this.login.cid}/playout/${this.login.pid}?hide=state`,
            {
                method: 'GET',
                headers: {
                    accept: 'application/json',
                },
            },
        );

        let pl: Playlist = {
            name: playlist.name,
            description: playlist.description,
            resolution: playlist.resolution,
            template_name: playlist.template_name,
            logo: playlist.logo,
            display_safearea: playlist.display_safearea,
            playlist: playlist.playlist.slice(),
            map_detail: playlist.map_detail,
            audio_url: '',
        };

        if (
            playlist.audio_playlist_id &&
            playlist.audio_playlist_id.length !== 0
        ) {
            pl.audio_url = `${this.streamingUrl}/memfs/${this.login.cid}/audio/playlist/${playlist.audio_playlist_id}/live/main.m3u8?token=${this.streamingToken}`;
        }

        return pl;
    }

    async fetchContent(url: string): Promise<any> {
        return await this.fetch(url, {
            method: 'GET',
            headers: {
                accept: 'application/json',
            },
        });
    }

    async getContent(
        request: PlaylistItemRequest,
    ): Promise<PlaylistItemContent> {
        const {
            content_id,
            content_type,
            index,
            playback_sec,
            content_url,
            enable_audio,
        } = request;

        try {
            // Kameradaten abrufen
            let data = null;
            if (content_type === 'live') {
                data = await this.fetchContent(
                    `https://cm.livespotting.com/api/v1/customer/${this.login.cid}/camera/${content_id}?hide=ingest`,
                );
            } else if (content_type === 'video_importer') {
                data = await this.fetchContent(
                    `https://cm.livespotting.com/api/v1/customer/${this.login.cid}/video_importer/location/${content_id}`,
                );
            } else if (content_type === 'live_recording') {
                data = await this.fetchContent(
                    `https://cm.livespotting.com/api/v1/customer/${this.login.cid}/video?camera_id=${content_id}&limit=1&hide=video_source`,
                );
            } else if (content_type === 'external_url') {
                data = {
                    id: content_id,
                    videosrc: content_url,
                    meta: null,
                };
            } else {
                throw new Error(`Unbekannter content_type: ${content_type}`);
            }

            if (!data) {
                throw new Error('Kameradaten nicht gefunden');
            }

            const days = [
                'Sonntag',
                'Montag',
                'Dienstag',
                'Mittwoch',
                'Donnerstag',
                'Freitag',
                'Samstag',
            ];
            const today = new Date();
            const day1 = days[today.getDay() % 7];
            const day2 = days[today.getDay() % 7].substring(0, 2);
            const day3 = days[(today.getDay() + 1) % 7].substring(0, 2);
            const day4 = days[(today.getDay() + 2) % 7].substring(0, 2);

            // Prüfen, ob die Kamera online ist und die entsprechende Auflösung verfügbar ist
            let source: { src: string; type: string } = { src: '', type: '' };
            if (content_type === 'live') {
                if (data.status !== 'healthy') {
                    throw new Error(`unhealthy camera: id=${data.id}`);
                }
                source = {
                    src: `${this.streamingUrl}/memfs/${this.login.cid}/${
                        data.id
                    }/live/${this.getBestProfile(data.streams)}.m3u8?token=${
                        this.streamingToken
                    }`,
                    type: 'application/x-mpegURL',
                };
            } else if (content_type === 'video_importer') {
                try {
                    source = {
                        src: `${data.latest_video?.video_sources[0]?.video_source}?token=${this.streamingToken}`,
                        type: 'video/mp4',
                    };
                } catch (error) {
                    throw new Error(
                        `Fehler beim Abrufen der Import-URL: ${error}`,
                    );
                }
            } else if (content_type === 'live_recording') {
                if (data.length == 0) {
                    throw new Error(`Die Kamera hat keine Aufnahme-URLs`);
                }

                try {
                    source = {
                        src: `${data[0]?.video_sources[0]?.video_source}?token=${this.streamingToken}`,
                        type: 'video/mp4',
                    };
                    data.id = data[0]?.camera_id || content_id;
                    data.meta = data[0]?.camera_meta || {};
                } catch (error) {
                    throw new Error(
                        `Fehler beim Abrufen der Aufnahme-URL: ${error}`,
                    );
                }
            } else if (content_type === 'external_url') {
                source = {
                    src: data.videosrc,
                    type: '',
                };
            }

            const content: PlaylistItemContent = {
                id: data.id,
                type: content_type,
                index: index,
                metadata: {
                    content_id: data.id,
                    index: index,
                },
                source: source,
                playback_sec: playback_sec,
                enable_audio: enable_audio,
            };

            if (data.meta) {
                content.metadata = {
                    content_id: data.id,
                    index: index,
                    location_name: data.meta.name_short || '',
                    location_producer: data.meta.producer || 'livespotting.com',
                    location_banner: data.meta.logo_url || '',
                    location_city: data.meta.city || '',
                    location_county: data.meta.county || '',
                    location_region: data.meta.region || '',
                    location_country: data.meta.country || '',
                    logo_background_color:
                        data.meta.logo_background_color || '#ffffff',
                    location_website:
                        data.meta.website.split('://')[1].split('/')[0] || '',
                    location_weather_icon36:
                        data?.weather?.forecast[0].icon || '',
                    location_weather_tmax36:
                        data?.weather?.forecast[0].temperature.max || '',
                    location_weather_tmin36:
                        data?.weather?.forecast[0].temperature.min || '',
                    location_weather_icon60:
                        data?.weather?.forecast[1].icon || '',
                    location_weather_tmax60:
                        data?.weather?.forecast[1].temperature.max || '',
                    location_weather_tmin60:
                        data?.weather?.forecast[1].temperature.min || '',
                    location_weather_icon84:
                        data?.weather?.forecast[2].icon || '',
                    location_weather_tmax84:
                        data?.weather?.forecast[2].temperature.max || '',
                    location_weather_tmin84:
                        data?.weather?.forecast[2].temperature.min || '',
                    location_today: day1 || '',
                    location_weather_day1: day2 || '',
                    location_weather_day2: day3 || '',
                    location_weather_day3: day4 || '',
                    longitude: data.meta.longitude || 0,
                    latitude: data.meta.latitude || 0,
                    country_code: data.meta.country_code || '',
                    location_country_code: data.meta.country_code || '',
                };
            }

            return content;
        } catch (error) {
            throw error;
        }
    }

    getBestProfile(streams: any[]): string {
        // Über die Streams iterieren und den ersten TV-Stream zurückgeben
        for (const stream of streams) {
            if (stream.profile === 'tv') {
                return 'tv_0';
            }
        }

        // Funktion zum Umrechnen der Auflösung in Gesamtpixel
        const resolutionToPixels = (resolution: string) => {
            const [width, height] = resolution.split('x').map(Number);
            return width * height;
        };

        // Streams nach Auflösung in absteigender Reihenfolge sortieren
        const sortedStreams = streams.sort((a, b) => {
            return (
                resolutionToPixels(b.resolution) -
                resolutionToPixels(a.resolution)
            );
        });

        // Über die sortierten Streams iterieren
        for (const stream of sortedStreams) {
            if (parseFloat(stream.fps) > 22) {
                return stream.profile;
            }
        }

        // Falls kein Stream die Bedingung erfüllt, null zurückgeben
        return '0';
    }

    async sendState(req: SendStateRequest): Promise<any> {
        return null;

        const { position } = req;

        try {
            await this.fetch(
                `https://cm.livespotting.com/api/v1/customer/${this.login.cid}/playout/${this.login.pid}/state`,
                {
                    method: 'POST',
                    headers: {
                        accept: 'application/json',
                        'content-type': 'application/json',
                        Authorization: `Bearer ${this.accessToken}`,
                    },
                    body: JSON.stringify({
                        position: position,
                    }),
                },
            );
        } catch (error) {
            console.error('error sending state data:', error);
        }

        return null;
    }

    async sendTracking(req: SendTrackingRequest): Promise<any> {
        return null;

        const { content_id, content_type, start_date, end_date } = req;

        try {
            await this.fetch(
                `https://cm.livespotting.com/api/v1/customer/${this.login.cid}/playout/${this.login.pid}/tracking`,
                {
                    method: 'POST',
                    headers: {
                        accept: 'application/json',
                        'content-type': 'application/json',
                        Authorization: `Bearer ${this.accessToken}`,
                    },
                    body: JSON.stringify({
                        content_id: content_id,
                        content_type: content_type || 'live',
                        mode: 'live',
                        start_date: start_date,
                        end_date: end_date,
                    }),
                },
            );
        } catch (error) {
            console.error('error sending tracking data:', error);
        }

        return null;
    }
}
